import { motion } from "framer-motion";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Search,
  GraduationCap,
  Users,
  Clock,
  Loader2,
  AlertCircle,
} from "lucide-react";

import { useScholarships } from "@/services/scholarshipService";
import type { RootState } from "@/store";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import BackToTop from "@/components/common/BackToTop";

interface ScholarshipsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
}

// Helper function to strip HTML tags for preview
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const Scholarships: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const queryParams: ScholarshipsQueryParams = {
    per_page: 12,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
    ...(statusFilter && { status: statusFilter }),
  };

  const {
    data: scholarshipsData,
    isLoading,
    error,
  } = useScholarships(queryParams);

  const scholarships = scholarshipsData?.scholarships || [];
  const pagination = scholarshipsData?.pagination;


  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "inactive":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "expired":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-emerald-600 via-stone-600 to-emerald-700 text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                {t("scholarships.title")}
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                {t("scholarships.subtitle")}
              </p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                      <input
                        type="text"
                        placeholder={t("scholarships.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                  <div className="md:w-48">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    >
                      <option value="">{t("scholarships.allStatuses")}</option>
                      <option value="active">
                        {t("scholarships.status.active")}
                      </option>
                      <option value="inactive">
                        {t("scholarships.status.inactive")}
                      </option>
                      <option value="expired">
                        {t("scholarships.status.expired")}
                      </option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Scholarships Grid */}
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t("common.loading")}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-20 px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md mx-auto"
              >
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 dark:text-red-400 text-lg font-semibold">
                  {t("scholarships.errorLoading")}
                </p>
              </motion.div>
            </div>
          ) : scholarships.length === 0 ? (
            <div className="text-center py-16 px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-2xl mx-auto"
              >
                <div className="mb-6">
                  <GraduationCap className="w-24 h-24 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {t("scholarships.noScholarshipsTitle")}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg mb-8 leading-relaxed">
                  {t("scholarships.noScholarships")}
                </p>
              </motion.div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {scholarships.map((scholarship, index) => {
                  const translation = scholarship.translations?.[0];
                  const activeSession = scholarship.sessions?.find(s => s.is_active) || scholarship.sessions?.[0];
                  const mainSponsor = activeSession?.sponsors?.find(sp => sp.is_main) || activeSession?.sponsors?.[0];

                  return (
                    <motion.div
                      key={scholarship.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                        <div className="relative">
                          {mainSponsor?.picture ? (
                            <img
                              src={mainSponsor.picture}
                              alt={mainSponsor.name}
                              className="w-full h-48 object-cover rounded-t-lg"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-r from-emerald-500 to-stone-500 rounded-t-lg flex items-center justify-center">
                              <GraduationCap className="w-16 h-16 text-white" />
                            </div>
                          )}
                          <div className="absolute top-4 right-4">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                scholarship.status
                              )}`}
                            >
                              {t(`scholarships.status.${scholarship.status}`)}
                            </span>
                          </div>
                        </div>

                        <CardContent className="flex-1 p-6">
                          <div className="mb-4">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                              {translation?.title || "Untitled Scholarship"}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {t("scholarships.by")} {mainSponsor?.name || "N/A"}
                            </p>
                          </div>

                          <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-3">
                            {translation?.description
                              ? stripHtml(translation.description)
                              : t("scholarships.noDescription")}
                          </p>

                          <div className="space-y-2 mb-6">
                            {activeSession?.academic_year && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Clock className="w-4 h-4" />
                                <span>
                                  {t("scholarships.academicYear", "Academic Year")}:{" "}
                                  {activeSession.academic_year}
                                </span>
                              </div>
                            )}

                            {activeSession?.beneficiaries &&
                              activeSession.beneficiaries.length > 0 && (
                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <Users className="w-4 h-4" />
                                  <span>
                                    {activeSession.beneficiaries.length}{" "}
                                    {t("scholarships.beneficiaries")}
                                  </span>
                                </div>
                              )}
                          </div>

                          <Link
                            to={`/${currentLanguage}/scholarships/${scholarship.id}`}
                            className="w-full"
                          >
                            <Button className="w-full bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white">
                              {t("scholarships.viewDetails")}
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.last_page > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex justify-center items-center gap-2"
                >
                  <Button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("common.previous")}
                  </Button>

                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                    .filter((page) => {
                      const start = Math.max(1, currentPage - 2);
                      const end = Math.min(
                        pagination.last_page,
                        currentPage + 2
                      );
                      return page >= start && page <= end;
                    })
                    .map((page) => (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        variant={page === currentPage ? "default" : "outline"}
                        className={
                          page === currentPage
                            ? "bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white shadow-lg"
                            : ""
                        }
                      >
                        {page}
                      </Button>
                    ))}

                  <Button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(pagination.last_page, currentPage + 1)
                      )
                    }
                    disabled={currentPage === pagination.last_page}
                    variant="outline"
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("common.next")}
                  </Button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

Scholarships.displayName = "Scholarships";

export default Scholarships;
